import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import { api, errorMessage } from "../../shared/api/client";
import type {
  ChurchFamily,
  ChurchFamilyMember,
  ChurchMember,
} from "../../shared/api/types";
import {
  Alert,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  Loading,
} from "../../shared/ui/components";
import { useAsync, type AsyncState } from "../../shared/ui/useAsync";
import { useLiveVersion } from "../../shared/live";
import { FAMILY_ROLE_KEYS, familyRoleLabel } from "./familyRoles";
import { fullName } from "./roles";
import { SearchSelect } from "../../shared/ui/SearchSelect";
import { useTabParam } from "../../shared/ui/useTabParam";
import { FamilyTreeView } from "./FamilyTree";

type Families = AsyncState<ChurchFamily[]> & { reload: () => void };

function RoleSelect({
  id,
  name,
  value,
  onChange,
}: {
  id: string;
  name?: string;
  value?: string;
  onChange?: (v: string) => void;
}) {
  return (
    <select
      id={id}
      name={name}
      value={value}
      defaultValue={value === undefined ? "hijo" : undefined}
      onChange={onChange ? (e) => onChange(e.target.value) : undefined}
    >
      {FAMILY_ROLE_KEYS.map((k) => (
        <option key={k} value={k}>
          {familyRoleLabel(k)}
        </option>
      ))}
    </select>
  );
}

type Candidate = { personId: string; givenName: string; familyName: string };

const MEMBERSHIP: Record<string, string> = {
  active: "Miembro",
  pending: "Solicitud pendiente",
  rejected: "Solicitud rechazada",
  ended: "Ya no es miembro",
};

function age(birthDate: string): number {
  const [y, m, d] = birthDate.split("-").map(Number) as [
    number,
    number,
    number,
  ];
  const now = new Date();
  return (
    now.getFullYear() -
    y -
    (now.getMonth() + 1 < m || (now.getMonth() + 1 === m && now.getDate() < d)
      ? 1
      : 0)
  );
}

/** Lo que distingue a cada quien: edad, cómo entra a KEORA, si es miembro y su clase. */
function memberDetails(m: ChurchFamilyMember): string {
  const parts: string[] = [];
  if (m.birthDate) parts.push(`${age(m.birthDate)} años`);
  if (m.access === "correo" && m.email)
    parts.push(`Entra con su correo ${m.email}`);
  else if (m.access === "celular")
    parts.push("Entra con su celular, ligado por papá o mamá");
  else if (m.access === "sin_cuenta")
    parts.push("Sin cuenta: entra por Mi familia con carita y PIN");
  parts.push(
    m.membership ? (MEMBERSHIP[m.membership] ?? m.membership) : "No es miembro",
  );
  if (m.classes?.length) parts.push(`Clase: ${m.classes.join(", ")}`);
  return parts.join(" · ");
}

/** Dentro de «Árbol familiar»: de qué familia viene cada adulto (papás, abuelos…). */
function OriginPicker({
  churchId,
  family,
  allFamilies,
  busy,
  run,
}: {
  churchId: string;
  family: ChurchFamily;
  allFamilies: ChurchFamily[];
  busy: boolean;
  run: (action: () => Promise<unknown>) => Promise<void>;
}) {
  const adults = family.members.filter(
    (m) => m.relationship !== "hijo" && m.relationship !== "hija",
  );
  const others = allFamilies.filter((f) => f.id !== family.id);
  if (adults.length === 0 || others.length === 0) return null;
  return (
    <div className="family-origins">
      <p className="small muted">De qué familia viene cada uno:</p>
      <ul className="member-list">
        {adults.map((m) => (
          <li key={m.personId} className="family-member">
            <label
              className="family-member__name"
              htmlFor={`origin-${family.id}-${m.personId}`}
            >
              {fullName(m)}
            </label>
            <select
              id={`origin-${family.id}-${m.personId}`}
              aria-label={`Familia de donde viene ${fullName(m)}`}
              value={m.originFamilyId ?? ""}
              disabled={busy}
              onChange={(e) =>
                void run(() =>
                  api.put(
                    `/multichurch/churches/${churchId}/members/${m.personId}/origin`,
                    { familyId: e.target.value || null },
                  ),
                )
              }
            >
              <option value="">Sin indicar</option>
              {others.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FamilyCard({
  churchId,
  family,
  people,
  familyOf,
  allFamilies,
  canManage,
  startOpen,
  onChanged,
}: {
  churchId: string;
  family: ChurchFamily;
  people: Candidate[];
  familyOf: Map<string, string>;
  allFamilies: ChurchFamily[];
  canManage: boolean;
  startOpen: boolean;
  onChanged: () => void;
}) {
  // Cerrada se ve en una línea: quiénes son y si alguien espera aprobación. Se abre para editarla.
  const [open, setOpen] = useState(startOpen);
  const [tree, setTree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const base = `/multichurch/churches/${churchId}/families/${family.id}`;
  // Cada persona está en una sola familia: si ya tiene otra, se mueve aquí.
  const available = people.filter(
    (p) => !family.members.some((m) => m.personId === p.personId),
  );

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      onChanged();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  function add(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const personId = String(form.get("personId") ?? "");
    if (!personId) {
      setError("Elige a la persona.");
      return;
    }
    const from = familyOf.get(personId);
    const who = fullName(people.find((p) => p.personId === personId)!);
    if (
      from &&
      !window.confirm(`¿Mover a ${who} a ${family.name}? Saldrá de ${from}.`)
    )
      return;
    void run(() =>
      api.put(`${base}/members/${personId}`, {
        relationship: String(form.get("relationship")),
        move: Boolean(from),
      }),
    );
  }

  function rename() {
    const name = window.prompt("Nuevo nombre de la familia", family.name);
    if (name === null || !name.trim()) return;
    void run(() => api.patch(base, { name: name.trim() }));
  }

  function remove() {
    if (
      !window.confirm(
        `¿Borrar la familia "${family.name}"? Las personas siguen en la iglesia; solo se quita el grupo.`,
      )
    )
      return;
    void run(() => api.delete(base));
  }

  const waiting = family.members.filter(
    (m) => m.membership === "pending",
  ).length;
  if (!open) {
    return (
      <Card
        title={family.name}
        action={
          <Button
            variant="ghost"
            onClick={() => setOpen(true)}
            aria-label={`Abrir ${family.name}`}
          >
            Abrir
          </Button>
        }
      >
        <p className="small">
          {family.members.length === 0 ? (
            <span className="muted">Sin integrantes</span>
          ) : (
            family.members.map((m) => m.givenName || fullName(m)).join(", ")
          )}
          {waiting > 0 ? (
            <span className="badge">
              {" "}
              {waiting === 1 ? "1 por aprobar" : `${waiting} por aprobar`}
            </span>
          ) : null}
        </p>
      </Card>
    );
  }

  return (
    <Card
      title={family.name}
      action={
        canManage ? (
          <div className="actions">
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cerrar
            </Button>
            <Button variant="ghost" onClick={() => setTree((t) => !t)}>
              {tree ? "Ocultar árbol" : "Árbol familiar"}
            </Button>
            <Button variant="ghost" onClick={rename} disabled={busy}>
              Renombrar
            </Button>
            <Button variant="ghost" onClick={remove} disabled={busy}>
              Borrar familia
            </Button>
          </div>
        ) : (
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cerrar
          </Button>
        )
      }
    >
      {error ? <Alert>{error}</Alert> : null}
      {family.linked ? (
        <p className="muted small">
          La formaron en Mi familia. Lo que cambies aquí (nombre, quién está,
          hijos que muevas) también se ve allá.
        </p>
      ) : null}
      {family.members.length === 0 ? (
        <p className="muted">Todavía no tiene integrantes.</p>
      ) : null}
      {tree ? (
        <FamilyTreeView
          rootId={family.id}
          families={allFamilies.map((f) => ({
            id: f.id,
            name: f.name,
            members: f.members.map((m) => ({
              personId: m.personId,
              name: fullName(m),
              originFamilyId: m.originFamilyId ?? null,
            })),
          }))}
        />
      ) : null}
      {tree && canManage ? (
        <OriginPicker
          churchId={churchId}
          family={family}
          allFamilies={allFamilies}
          busy={busy}
          run={run}
        />
      ) : null}
      <ul className="member-list">
        {family.members.map((m) => (
          <li key={m.personId} className="family-member">
            <span className="family-member__name">
              <Link to={`/app/miembros/${m.personId}`}>{fullName(m)}</Link>
              <span className="muted small family-member__details">
                {memberDetails(m)}
              </span>
            </span>
            {canManage ? (
              <>
                <label
                  className="visually-hidden"
                  htmlFor={`role-${family.id}-${m.personId}`}
                >
                  Parentesco de {fullName(m)}
                </label>
                <RoleSelect
                  id={`role-${family.id}-${m.personId}`}
                  value={m.relationship}
                  onChange={(relationship) =>
                    void run(() =>
                      api.put(`${base}/members/${m.personId}`, {
                        relationship,
                      }),
                    )
                  }
                />
                <Button
                  variant="ghost"
                  onClick={() => {
                    if (
                      !window.confirm(
                        m.fromHome
                          ? `¿Quitar a ${fullName(m)} de ${family.name}? También saldrá de su Mi familia.`
                          : `¿Quitar a ${fullName(m)} de ${family.name}?`,
                      )
                    )
                      return;
                    void run(() => api.delete(`${base}/members/${m.personId}`));
                  }}
                  disabled={busy}
                  aria-label={`Quitar a ${fullName(m)} de la familia`}
                >
                  Quitar
                </Button>
              </>
            ) : (
              <span className="badge">{familyRoleLabel(m.relationship)}</span>
            )}
          </li>
        ))}
      </ul>
      {canManage && available.length > 0 ? (
        <form className="family-add" onSubmit={add}>
          <div className="field">
            <label htmlFor={`add-person-${family.id}`}>
              Agregar integrante
            </label>
            <SearchSelect
              id={`add-person-${family.id}`}
              name="personId"
              options={available.map((p) => ({
                value: p.personId,
                label: familyOf.has(p.personId)
                  ? `${fullName(p)} (en ${familyOf.get(p.personId)})`
                  : fullName(p),
              }))}
            />
          </div>
          <div className="field">
            <label htmlFor={`add-role-${family.id}`}>Parentesco</label>
            <RoleSelect id={`add-role-${family.id}`} name="relationship" />
          </div>
          <Button type="submit" busy={busy}>
            Agregar
          </Button>
        </form>
      ) : null}
    </Card>
  );
}

/** Familias de la iglesia: agrupar miembros y etiquetar su parentesco. */
type Filter = "todas" | "mi_familia" | "a_mano";
const FILTERS: [Filter, string][] = [
  ["todas", "Todas"],
  ["mi_familia", "De Mi familia"],
  ["a_mano", "Armadas aquí"],
];

const plain = (t: string) =>
  t
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

type FamilyChangeRequest = {
  id: string;
  name: string;
  description: string;
  createdAt: string;
};

/** Lo que los miembros piden cambiar en Mi familia: se aprueba o se rechaza en Miembros → Por aprobar. */
export function FamilyChangeRequests({
  churchId,
  onDecided,
}: {
  churchId: string;
  onDecided: () => void;
}) {
  const live = useLiveVersion((e) => e.kind === "family.request");
  const requests = useAsync(
    (signal) =>
      api
        .get<{
          items: FamilyChangeRequest[];
        }>(`/multichurch/churches/${churchId}/family-requests`, signal)
        .then((r) => r.items),
    [churchId, live],
  );
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function decide(r: FamilyChangeRequest, approve: boolean) {
    setBusyId(r.id);
    setError(null);
    try {
      await api.post(
        `/multichurch/churches/${churchId}/family-requests/${r.id}/${approve ? "approve" : "reject"}`,
        approve ? undefined : { reason: reason.trim() || undefined },
      );
      setRejecting(null);
      setReason("");
      requests.reload();
      onDecided();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusyId(null);
    }
  }

  if (requests.status !== "ready" || requests.data.length === 0) return null;
  return (
    <Card title="Cambios que piden las familias">
      {error ? <Alert>{error}</Alert> : null}
      <ul className="list">
        {requests.data.map((r) => (
          <li key={r.id} className="list__item list__item--actions">
            <div>
              <strong>{r.name}</strong> pide {r.description}.
            </div>
            {rejecting === r.id ? (
              <div className="reject-box">
                <div className="field">
                  <label htmlFor={`family-reason-${r.id}`}>
                    Motivo (opcional; lo verá la persona)
                  </label>
                  <textarea
                    id={`family-reason-${r.id}`}
                    rows={2}
                    maxLength={500}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                </div>
                <div className="actions">
                  <Button
                    variant="danger"
                    busy={busyId === r.id}
                    disabled={busyId !== null}
                    onClick={() => void decide(r, false)}
                  >
                    Rechazar
                  </Button>
                  <Button variant="ghost" onClick={() => setRejecting(null)}>
                    Cancelar
                  </Button>
                </div>
              </div>
            ) : (
              <div className="actions">
                <Button
                  variant="secondary"
                  disabled={busyId !== null}
                  onClick={() => setRejecting(r.id)}
                >
                  Rechazar
                </Button>
                <Button
                  busy={busyId === r.id}
                  disabled={busyId !== null}
                  onClick={() => void decide(r, true)}
                >
                  Aprobar
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}

export function FamiliesTab({
  churchId,
  families,
  people,
  canManage,
  canApprove = false,
  onApproved,
}: {
  churchId: string;
  families: Families;
  people: ChurchMember[];
  canManage: boolean;
  canApprove?: boolean;
  onApproved?: () => void;
}) {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useTabParam(
    FILTERS.map(([k]) => k),
    "familias",
  );
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const q = plain(query.trim());
  const shown =
    families.status === "ready"
      ? families.data
          .filter((f) => filter !== "mi_familia" || f.linked)
          .filter((f) => filter !== "a_mano" || !f.linked)
          .filter(
            (f) =>
              !q ||
              plain(f.name).includes(q) ||
              f.members.some((m) => plain(fullName(m)).includes(q)),
          )
      : [];
  const pendingKids =
    families.status === "ready"
      ? new Set(
          families.data.flatMap((f) =>
            f.members
              .filter((m) => m.membership === "pending")
              .map((m) => m.personId),
          ),
        ).size
      : 0;
  const familyOf = new Map(
    families.status === "ready"
      ? families.data.flatMap((f) =>
          f.members.map((m) => [m.personId, f.name] as const),
        )
      : [],
  );
  // Miembros y también quienes ya están en alguna familia (p. ej. hijos que aún no son miembros), para poder moverlos.
  const candidates = new Map<string, Candidate>(
    people.map((p) => [p.personId, p]),
  );
  if (families.status === "ready")
    for (const f of families.data)
      for (const m of f.members)
        if (!candidates.has(m.personId)) candidates.set(m.personId, m);

  async function create(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formEl = event.currentTarget;
    const name = String(new FormData(formEl).get("name") ?? "").trim();
    if (!name) {
      setError("Escribe el nombre de la familia.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post(`/multichurch/churches/${churchId}/families`, { name });
      formEl.reset();
      setCreating(false);
      families.reload();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {canManage && creating ? (
        <Card title="Nueva familia">
          <form className="form form--inline" onSubmit={create} noValidate>
            {error ? <Alert>{error}</Alert> : null}
            <p className="muted small">
              Las familias que forman en Mi familia aparecen aquí solas. Aquí
              puedes armar otras, cambiarles el nombre y mover a cada persona a
              su familia.
            </p>
            <Field
              label="Nombre"
              name="name"
              placeholder="Familia López García"
              maxLength={120}
            />
            <div className="actions">
              <Button type="submit" busy={busy}>
                Crear familia
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setCreating(false)}
              >
                Cancelar
              </Button>
            </div>
          </form>
        </Card>
      ) : null}
      <div className="family-tools">
        <div className="field">
          <label htmlFor="family-search">Buscar familia o persona</label>
          <input
            id="family-search"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Ej. Rivera o Diego"
          />
        </div>
        <div className="tabs" role="tablist" aria-label="Filtrar familias">
          {FILTERS.map(([k, label]) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={filter === k}
              className={`tabs__tab${filter === k ? " tabs__tab--active" : ""}`}
              onClick={() => setFilter(k)}
            >
              {label}
            </button>
          ))}
        </div>
        {canManage && !creating ? (
          <Button variant="secondary" onClick={() => setCreating(true)}>
            Nueva familia
          </Button>
        ) : null}
      </div>
      {canApprove && pendingKids > 0 ? (
        <Alert tone="info">
          {pendingKids === 1
            ? "Una persona de estas familias espera"
            : `${pendingKids} personas de estas familias esperan`}{" "}
          tu aprobación para ser miembro. Apruébalas en la pestaña «Por
          aprobar».
        </Alert>
      ) : null}
      {families.status === "loading" ? <Loading /> : null}
      {families.status === "error" ? (
        <ErrorState error={families.error} onRetry={families.reload} />
      ) : null}
      {families.status === "ready" && families.data.length === 0 ? (
        <EmptyState title="Todavía no hay familias.">
          <p className="muted">
            Crea una familia y agrega a sus integrantes con su parentesco: papá,
            hija, primo…
          </p>
        </EmptyState>
      ) : null}
      {families.status === "ready" &&
      families.data.length > 0 &&
      shown.length === 0 ? (
        <p className="muted">Ninguna familia coincide.</p>
      ) : null}
      {families.status === "ready" && families.data.length > 0 ? (
        <p className="muted small">
          {shown.length === families.data.length
            ? `${shown.length} familias`
            : `${shown.length} de ${families.data.length} familias`}
        </p>
      ) : null}
      {families.status === "ready"
        ? shown.map((f) => (
            <FamilyCard
              key={`${f.id}:${q && shown.length <= 3 ? "abierta" : ""}`}
              churchId={churchId}
              family={f}
              people={[...candidates.values()]}
              familyOf={familyOf}
              allFamilies={families.status === "ready" ? families.data : []}
              canManage={canManage}
              startOpen={Boolean(q) && shown.length <= 3}
              onChanged={() => {
                families.reload();
                onApproved?.();
              }}
            />
          ))
        : null}
    </>
  );
}
